import { coreGameStateKey, parseGameIdentity } from '@/lib/gameState/admission';
import type { InjuryView } from '@/lib/gameState/injuries';
import type { GameStateConnectionStatus } from '@/lib/gameState/transport';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { useCoreGameStateInterest, useCoreGameStatesStore } from '@/services/coreGameStates';

const stamp = (value: string) => new Date(value).toISOString().replace('T', ' ').replace('.000Z', ' UTC');

export function GameInjuriesView({ row, status, now }: {
  row?: InjuryView; status: GameStateConnectionStatus; now: number;
}) {
  const block = row?.block;
  const teams = block ? Object.entries(block.teams) : [];
  const count = teams.reduce((n, [, team]) => n + team.players.length, 0);
  const pending = teams.filter(([, team]) => !team.filed).length;
  const freshness = row?.invalid ? 'Invalid update · last known'
    : status !== 'live' ? 'Disconnected · last known'
      : row && now - row.receivedAt > 15_000 ? 'Refresh overdue' : null;
  return (
    <details className="border-border min-w-0 shrink-0 border-t text-xs" aria-label="Injury reports">
      <summary className="cursor-pointer px-3 py-2 break-words">
        <span className="font-semibold">Injuries</span>
        <span className="text-muted-foreground ml-2">{!block ? 'Report unavailable'
          : `${count} player${count === 1 ? '' : 's'} reported${pending ? ` · ${pending} team${pending === 1 ? '' : 's'} pending` : ''}`}</span>
        {block && freshness && <span className="text-amber-500 ml-2">{freshness}</span>}
      </summary>
      <div className="max-h-64 overflow-auto overscroll-contain px-3 pb-2 break-words [overflow-wrap:anywhere]" tabIndex={0} aria-label="Injury report details; scroll for more">
        {!block ? <p className="text-muted-foreground">No injury report is available for this game.</p> : <>
          <p className="text-muted-foreground">{block.source} · Report <time dateTime={block.reportAt}>{stamp(block.reportAt)}</time></p>
          {teams.map(([code, team]) => <section key={code} className="mt-2" aria-label={`${code} injury report`}>
            <h4 className="font-semibold">{code}</h4>
            {!team.filed ? <p>Report not yet submitted.</p> : team.players.length === 0 ? <p>No reported injuries.</p> : <ul className="space-y-2">
              {team.players.map((player, index) => <li key={`${player.player}-${index}`}>
                <div><span className="font-medium">{player.player}</span> · {player.statusRaw}</div>
                {player.reason && <p className="text-muted-foreground">{player.reason}</p>}
                <div className="text-muted-foreground text-[10px]">Status since <time dateTime={player.statusSince}>{stamp(player.statusSince)}</time>
                  {player.pairing !== 'paired' && ` · ${player.pairing === 'ambiguous' ? 'Ambiguous player match' : 'Player not linked'}`}</div>
                {player.source !== block.source && <div className="text-muted-foreground">{player.source}</div>}
              </li>)}
            </ul>}
          </section>)}
        </>}
      </div>
    </details>
  );
}

export function GameInjuries({ gameKey }: { gameKey: string | null }) {
  useCoreGameStateInterest(gameKey);
  const configured = useCoreGameStatesStore(s => s.configured);
  const key = coreGameStateKey(gameKey);
  const row = useCoreGameStatesStore(s => key ? s.injuries.get(key) : undefined);
  const status = useCoreGameStatesStore(s => s.status);
  const now = useSharedNow(1000, configured);
  if (!configured || !key || parseGameIdentity(key)?.sport !== 'nba') return null;
  return <GameInjuriesView key={key} row={row} status={status} now={now} />;
}
